/* Broworkshop · núcleo: utilidades, API de Shopify, catálogo, búsqueda, carrito y favoritos */
(function () {
  'use strict';

  const C = window.BW_CONFIG;
  const BW = (window.BW = window.BW || {});

  /* ───────────── Modo: web independiente o tema de Shopify ───────────── */
  const IN_SHOPIFY = C.mode === 'shopify';
  const R = Object.assign({
    home: 'index.html',
    shop: 'tienda.html',
    search: 'tienda.html',
    product: 'producto.html?p=',
    policies: 'politicas.html',
    cart: '',
    checkout: '',
  }, C.routes || {});
  /** Página actual en Shopify: { type, product, collection, collectionTitle }. */
  const page = window.BW_PAGE || {};
  /** Dirección de una imagen del tema (en Shopify viene de asset_url). */
  const img = (name) => (C.img && C.img[name]) || `assets/img/${name}`;
  /** Une una ruta base con parámetros de búsqueda. */
  const link = (base, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return q ? `${base}${base.includes('?') ? '&' : '?'}${q}` : base;
  };

  /* ───────────── Utilidades ───────────── */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
  const norm = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const slug = (s) => norm(s).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const debounce = (fn, ms = 200) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  };
  const motionOK = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const params = () => new URLSearchParams(window.location.search);

  function makeStore(area) {
    return {
      get(key, fallback = null) {
        try {
          const raw = window[area].getItem(key);
          return raw == null ? fallback : JSON.parse(raw);
        } catch { return fallback; }
      },
      set(key, value) { try { window[area].setItem(key, JSON.stringify(value)); } catch { /* almacenamiento no disponible */ } },
      remove(key) { try { window[area].removeItem(key); } catch { /* almacenamiento no disponible */ } },
    };
  }
  const local = makeStore('localStorage');
  const session = makeStore('sessionStorage');

  const moneyFmt = new Intl.NumberFormat(C.locale, {
    style: 'currency', currency: C.currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
  });
  const money = (n) => moneyFmt.format(Math.round(Number(n) || 0));

  function imgUrl(url, width) {
    if (!url) return '';
    try {
      const u = new URL(url);
      u.searchParams.set('width', String(width));
      return u.href;
    } catch { return url; }
  }
  const srcset = (url, widths = [240, 360, 480, 720, 960]) =>
    url ? widths.map((w) => `${imgUrl(url, w)} ${w}w`).join(', ') : '';

  /* ───────────── Shopify Storefront API ───────────── */
  const ENDPOINT = `https://${C.shopDomain}/api/${C.apiVersion}/graphql.json`;
  const CTX_DEF = '$country: CountryCode, $language: LanguageCode';
  const CTX_USE = '@inContext(country: $country, language: $language)';

  async function gql(query, variables = {}) {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': C.storefrontToken,
      },
      body: JSON.stringify({ query, variables: { country: C.country, language: C.language, ...variables } }),
    });
    if (!res.ok) throw new Error(`Shopify respondió ${res.status}`);
    const json = await res.json();
    if (json.errors && json.errors.length) throw new Error(json.errors.map((e) => e.message).join(' · '));
    return json.data;
  }

  const CARD = `fragment Card on Product {
    id handle title productType vendor tags availableForSale createdAt
    priceRange { minVariantPrice { amount } maxVariantPrice { amount } }
    compareAtPriceRange { maxVariantPrice { amount } }
    featuredImage { url altText }
    images(first: 2) { nodes { url altText } }
    variants(first: 2) { nodes { id availableForSale } }
  }`;

  function mapProduct(p, rank = 0) {
    const price = Number(p.priceRange.minVariantPrice.amount);
    const compareRaw = Number(p.compareAtPriceRange?.maxVariantPrice?.amount || 0);
    const onSale = compareRaw > price;
    const imgs = p.images?.nodes?.length ? p.images.nodes : p.featuredImage ? [p.featuredImage] : [];
    const variants = p.variants?.nodes || [];
    const type = (p.productType || '').trim() || 'Varios';
    const tags = p.tags || [];
    return {
      id: p.id,
      handle: p.handle,
      title: p.title,
      type,
      typeSlug: slug(type),
      vendor: (p.vendor || '').trim(),
      tags,
      available: !!p.availableForSale,
      createdAt: p.createdAt || '',
      price,
      maxPrice: Number(p.priceRange.maxVariantPrice?.amount || price),
      compare: onSale ? compareRaw : 0,
      discount: onSale ? Math.round((1 - price / compareRaw) * 100) : 0,
      bestPrice: tags.some((t) => norm(t) === 'mejor precio'),
      image: imgs[0]?.url || '',
      image2: imgs[1]?.url || '',
      alt: imgs[0]?.altText || p.title,
      variantId: variants.length === 1 ? variants[0].id : null,
      multi: variants.length > 1,
      rank,
      key: norm([p.title, type, p.vendor, ...tags].join(' ')),
    };
  }

  /* ───────────── Catálogo (con caché de sesión) ───────────── */
  const CATALOG_QUERY = `query Catalog($cursor: String, ${CTX_DEF}) ${CTX_USE} {
    products(first: 250, after: $cursor, sortKey: BEST_SELLING) {
      pageInfo { hasNextPage endCursor }
      nodes { ...Card }
    }
  } ${CARD}`;
  const CATALOG_KEY = 'bw:catalog:v1';
  const CATALOG_TTL = 10 * 60 * 1000;
  const index = new Map();
  let catalogPromise = null;

  function register(list) {
    list.forEach((p) => { if (p && p.handle) index.set(p.handle, p); });
    return list;
  }

  function loadCatalog() {
    if (catalogPromise) return catalogPromise;
    catalogPromise = (async () => {
      const cached = session.get(CATALOG_KEY);
      if (cached && Array.isArray(cached.items) && Date.now() - cached.t < CATALOG_TTL) {
        return register(cached.items);
      }
      const items = [];
      let cursor = null;
      do {
        const data = await gql(CATALOG_QUERY, { cursor });
        data.products.nodes.forEach((n) => items.push(mapProduct(n, items.length)));
        cursor = data.products.pageInfo.hasNextPage ? data.products.pageInfo.endCursor : null;
      } while (cursor && items.length < 5000);
      session.set(CATALOG_KEY, { t: Date.now(), items });
      return register(items);
    })();
    catalogPromise.catch(() => { catalogPromise = null; });
    return catalogPromise;
  }

  /* ───────────── Categorías y marcas ───────────── */
  const TONES = ['sky', 'lav', 'mint', 'peach', 'butter', 'rose'];
  const CAT_META = {
    cocina: { icon: 'pot', tone: 'peach', blurb: 'Ollas, licuadoras, vajillas y más' },
    audio: { icon: 'headphones', tone: 'lav', blurb: 'Parlantes y audífonos' },
    electrodomesticos: { icon: 'washer', tone: 'sky', blurb: 'Aliados para las tareas de la casa' },
    computadores: { icon: 'laptop', tone: 'mint', blurb: 'Tecnología para trabajar y estudiar' },
    'cuidado-personal': { icon: 'sparkles', tone: 'rose', blurb: 'Secadores, planchas y más' },
    'tv-y-video': { icon: 'tv', tone: 'butter', blurb: 'Televisores y video' },
    'celulares-y-accesorios': { icon: 'phone', tone: 'sky', blurb: 'Celulares, cargadores y soportes' },
    'plancha-y-lavado': { icon: 'shirt', tone: 'mint', blurb: 'Cuidado de la ropa' },
    'hogar-y-muebles': { icon: 'sofa', tone: 'peach', blurb: 'Todo para tu casa' },
    'pilas-y-energia': { icon: 'bolt', tone: 'butter', blurb: 'Pilas y energía' },
    papeleria: { icon: 'pen', tone: 'rose', blurb: 'Oficina y estudio' },
    camaras: { icon: 'camera', tone: 'sky', blurb: 'Captura cada momento' },
    'morrales-y-maletas': { icon: 'backpack', tone: 'mint', blurb: 'Para llevar a todas partes' },
    'deportes-y-aire-libre': { icon: 'ball', tone: 'butter', blurb: 'Deporte y aire libre' },
    varios: { icon: 'gift', tone: 'lav', blurb: 'Hallazgos para ti' },
  };
  function catMeta(s) {
    if (CAT_META[s]) return CAT_META[s];
    const sum = [...String(s)].reduce((a, c) => a + c.charCodeAt(0), 0);
    return { icon: 'tag', tone: TONES[sum % TONES.length], blurb: 'Descubre esta categoría' };
  }

  function groupBy(items, keyFn, labelFn) {
    const map = new Map();
    for (const p of items) {
      const key = keyFn(p);
      if (!key) continue;
      let g = map.get(key);
      if (!g) { g = { key, name: labelFn(p), count: 0, items: [] }; map.set(key, g); }
      g.count += 1;
      g.items.push(p);
    }
    return [...map.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'es'));
  }
  const categoriesOf = (items) =>
    groupBy(items, (p) => p.typeSlug, (p) => p.type).map((g) => ({ ...g, slug: g.key, ...catMeta(g.key) }));
  const brandsOf = (items) => groupBy(items, (p) => p.vendor, (p) => p.vendor);

  /* ───────────── Búsqueda ───────────── */
  const tokenize = (q) => norm(q).split(/[^a-z0-9]+/).filter(Boolean);
  const stem = (t) => (t.length > 4 ? t.replace(/(es|s)$/, '') : t);

  function searchScore(p, tokens) {
    if (!tokens.length) return 1;
    const title = norm(p.title);
    let score = 0;
    for (const t of tokens) {
      const s = stem(t);
      if (title.startsWith(s)) score += 60;
      else if (new RegExp(`\\b${s}`).test(title)) score += 40;
      else if (title.includes(s)) score += 25;
      else if (p.key.includes(s)) score += 10;
      else return 0;
    }
    return score;
  }

  function lev(a, b) {
    const row = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
      let prev = i - 1;
      row[0] = i;
      for (let j = 1; j <= b.length; j++) {
        const tmp = row[j];
        row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
        prev = tmp;
      }
    }
    return row[b.length];
  }

  /** Si una búsqueda no da resultados, propone la palabra más parecida del catálogo. */
  function suggest(items, q) {
    const vocab = new Set();
    items.forEach((p) => tokenize(p.title).forEach((w) => { if (w.length > 2) vocab.add(w); }));
    const words = [...vocab];
    let changed = false;
    const fixed = tokenize(q).map((t) => {
      if (words.some((w) => w.includes(stem(t)))) return t;
      let best = null;
      let bestD = t.length > 5 ? 3 : 2;
      for (const w of words) {
        if (Math.abs(w.length - t.length) >= bestD) continue;
        const d = lev(t, w);
        if (d < bestD) { bestD = d; best = w; }
      }
      if (best) { changed = true; return best; }
      return t;
    });
    return changed ? fixed.join(' ') : null;
  }

  /** Resalta en <mark> las palabras buscadas (el texto se escapa siempre). */
  function highlight(text, q) {
    const tokens = tokenize(q).map(stem).filter((t) => t.length > 1);
    const n = norm(text);
    if (!tokens.length || n.length !== text.length) return esc(text);
    const marks = new Array(text.length).fill(false);
    tokens.forEach((t) => {
      let i = n.indexOf(t);
      while (i !== -1) { for (let k = i; k < i + t.length; k++) marks[k] = true; i = n.indexOf(t, i + t.length); }
    });
    let out = '';
    let open = false;
    for (let i = 0; i < text.length; i++) {
      if (marks[i] && !open) { out += '<mark>'; open = true; }
      if (!marks[i] && open) { out += '</mark>'; open = false; }
      out += esc(text[i]);
    }
    return open ? `${out}</mark>` : out;
  }

  /* ───────────── Ficha de producto ───────────── */
  const PRODUCT_QUERY = `query Product($handle: String!, ${CTX_DEF}) ${CTX_USE} {
    product(handle: $handle) {
      ...Card
      descriptionHtml
      description
      seo { title description }
      options { name optionValues { name swatch { color } } }
      gallery: images(first: 20) { nodes { url altText width height } }
      allVariants: variants(first: 100) {
        nodes {
          id title availableForSale
          price { amount }
          compareAtPrice { amount }
          selectedOptions { name value }
          image { url altText }
        }
      }
    }
  } ${CARD}`;
  const RECS_QUERY = `query Recs($id: ID!, ${CTX_DEF}) ${CTX_USE} {
    productRecommendations(productId: $id) { ...Card }
  } ${CARD}`;

  async function getProduct(handle) {
    const data = await gql(PRODUCT_QUERY, { handle });
    if (!data.product) return null;
    const card = mapProduct(data.product);
    register([card]);
    return { raw: data.product, card };
  }

  const COLLECTION_QUERY = `query Coll($handle: String!, $cursor: String, ${CTX_DEF}) ${CTX_USE} {
    collection(handle: $handle) {
      title
      products(first: 250, after: $cursor) { pageInfo { hasNextPage endCursor } nodes { handle } }
    }
  }`;

  /** Título y productos de una colección de Shopify. */
  async function getCollection(handle) {
    const handles = [];
    let title = '';
    let cursor = null;
    do {
      const data = await gql(COLLECTION_QUERY, { handle, cursor });
      if (!data.collection) return null;
      title = data.collection.title;
      data.collection.products.nodes.forEach((n) => handles.push(n.handle));
      cursor = data.collection.products.pageInfo.hasNextPage ? data.collection.products.pageInfo.endCursor : null;
    } while (cursor);
    return { title, handles };
  }

  async function getRecommendations(productId) {
    const data = await gql(RECS_QUERY, { id: productId });
    return register((data.productRecommendations || []).map((p, i) => mapProduct(p, i)));
  }

  function sanitizeHtml(html) {
    const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
    doc.querySelectorAll('script,style,object,embed,form,input,button,textarea,select,link,meta,base').forEach((n) => n.remove());
    doc.querySelectorAll('iframe').forEach((f) => {
      const src = f.getAttribute('src') || '';
      if (!/^https:\/\/(www\.)?(youtube(-nocookie)?\.com|player\.vimeo\.com)\//.test(src)) f.remove();
    });
    doc.querySelectorAll('*').forEach((el) => {
      [...el.attributes].forEach((a) => {
        const name = a.name.toLowerCase();
        const bad =
          name.startsWith('on') ||
          (name === 'href' && /^\s*(javascript|data|vbscript):/i.test(a.value)) ||
          ((name === 'src' || name === 'xlink:href') && /^\s*(javascript|vbscript):/i.test(a.value));
        if (bad) el.removeAttribute(a.name);
      });
      if (el.tagName === 'A') { el.setAttribute('rel', 'noopener'); el.setAttribute('target', '_blank'); }
    });
    return doc.body.firstElementChild ? doc.body.firstElementChild.innerHTML : '';
  }

  /* ───────────── Carrito (Cart API de Shopify) ───────────── */
  const CART_FIELDS = `fragment CartFields on Cart {
    id checkoutUrl totalQuantity
    cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
    lines(first: 100) {
      nodes {
        id quantity
        cost { totalAmount { amount } }
        merchandise {
          ... on ProductVariant {
            id title availableForSale
            price { amount }
            compareAtPrice { amount }
            image { url altText }
            product { title handle productType vendor featuredImage { url altText } }
          }
        }
      }
    }
  }`;
  const ERR = 'userErrors { field message code }';
  const Q_CART = `query Cart($id: ID!, ${CTX_DEF}) ${CTX_USE} { cart(id: $id) { ...CartFields } } ${CART_FIELDS}`;
  const M_CREATE = `mutation CartCreate($input: CartInput!, ${CTX_DEF}) ${CTX_USE} {
    cartCreate(input: $input) { cart { ...CartFields } ${ERR} } } ${CART_FIELDS}`;
  const M_ADD = `mutation CartAdd($id: ID!, $lines: [CartLineInput!]!, ${CTX_DEF}) ${CTX_USE} {
    cartLinesAdd(cartId: $id, lines: $lines) { cart { ...CartFields } ${ERR} } } ${CART_FIELDS}`;
  const M_UPDATE = `mutation CartUpdate($id: ID!, $lines: [CartLineUpdateInput!]!, ${CTX_DEF}) ${CTX_USE} {
    cartLinesUpdate(cartId: $id, lines: $lines) { cart { ...CartFields } ${ERR} } } ${CART_FIELDS}`;
  const M_REMOVE = `mutation CartRemove($id: ID!, $lineIds: [ID!]!, ${CTX_DEF}) ${CTX_USE} {
    cartLinesRemove(cartId: $id, lineIds: $lineIds) { cart { ...CartFields } ${ERR} } } ${CART_FIELDS}`;
  const CART_KEY = 'bw:cart';

  function createEmitter(target) {
    const subs = new Set();
    target.subscribe = (fn) => { subs.add(fn); return () => subs.delete(fn); };
    target.emit = (detail) => subs.forEach((fn) => {
      try { fn(target, detail); } catch (e) { console.error(e); }
    });
    return target;
  }

  /* Carrito nativo del tema: /cart.js, /cart/add.js y /cart/change.js */
  const AJAX_CART = IN_SHOPIFY && C.cartMode === 'ajax';
  const shopRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const variantNumber = (gid) => Number(String(gid).split('/').pop());

  async function ajaxCart(path, body) {
    const res = await fetch(`${shopRoot()}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.description || json.message || `Shopify respondió ${res.status}`);
    return json;
  }

  /** Convierte el carrito de /cart.js al mismo formato del carrito de la Storefront API. */
  function fromAjaxCart(c) {
    const nodes = (c.items || []).map((it) => {
      const unit = it.final_price / 100;
      return {
        id: it.key,
        quantity: it.quantity,
        cost: { totalAmount: { amount: it.final_line_price / 100 } },
        merchandise: {
          id: `gid://shopify/ProductVariant/${it.variant_id}`,
          title: it.variant_title || 'Default Title',
          availableForSale: true,
          price: { amount: unit },
          // El precio "antes" sale del catálogo (cart.js no lo trae).
          get compareAtPrice() {
            const known = index.get(it.handle);
            return known && known.compare > unit ? { amount: known.compare } : null;
          },
          image: it.image ? { url: it.image } : null,
          product: {
            title: it.product_title, handle: it.handle,
            productType: it.product_type || '', vendor: it.vendor || '', featuredImage: null,
          },
        },
      };
    });
    return {
      id: c.token,
      checkoutUrl: R.checkout || `${shopRoot()}checkout`,
      totalQuantity: c.item_count || 0,
      cost: {
        subtotalAmount: { amount: (c.items_subtotal_price || 0) / 100 },
        totalAmount: { amount: (c.total_price || 0) / 100 },
      },
      lines: { nodes },
    };
  }

  const Cart = createEmitter({
    data: null,
    ready: null,
    queue: Promise.resolve(),

    get count() { return this.data?.totalQuantity || 0; },
    get lines() { return this.data?.lines?.nodes || []; },

    init() {
      if (!this.ready) this.ready = this.refresh();
      return this.ready;
    },

    async refresh() {
      if (AJAX_CART) {
        try { this.data = fromAjaxCart(await ajaxCart('cart.js')); } catch (e) { console.warn('[carrito]', e); }
        this.emit({ type: 'sync' });
        return;
      }
      const id = local.get(CART_KEY);
      if (id) {
        try {
          const d = await gql(Q_CART, { id });
          this.data = d.cart || null;
          if (!d.cart) local.remove(CART_KEY);
        } catch (e) { console.warn('[carrito]', e); }
      }
      this.emit({ type: 'sync' });
    },

    run(task) {
      const next = this.queue.then(task, task);
      this.queue = next.catch(() => {});
      return next;
    },

    apply(payload) {
      const errors = payload?.userErrors || [];
      if (errors.length) throw new Error(errors.map((e) => e.message).join(' · '));
      if (!payload?.cart) throw new Error('El carrito no está disponible');
      this.data = payload.cart;
      local.set(CART_KEY, this.data.id);
      return this.data;
    },

    add(merchandiseId, quantity = 1) {
      return this.run(async () => {
        if (AJAX_CART) {
          await ajaxCart('cart/add.js', { items: [{ id: variantNumber(merchandiseId), quantity }] });
          this.data = fromAjaxCart(await ajaxCart('cart.js'));
          this.emit({ type: 'add', merchandiseId, quantity });
          return this.data;
        }
        await this.init();
        const lines = [{ merchandiseId, quantity }];
        let done = false;
        if (this.data?.id) {
          const payload = (await gql(M_ADD, { id: this.data.id, lines })).cartLinesAdd;
          if (payload?.cart) { this.apply(payload); done = true; }
          else { this.data = null; local.remove(CART_KEY); } // el carrito expiró: se crea uno nuevo
        }
        if (!done) {
          const input = { lines, buyerIdentity: { countryCode: C.country } };
          this.apply((await gql(M_CREATE, { input })).cartCreate);
        }
        this.emit({ type: 'add', merchandiseId, quantity });
        return this.data;
      });
    },

    update(lineId, quantity) {
      if (quantity <= 0) return this.remove(lineId);
      return this.run(async () => {
        if (AJAX_CART) this.data = fromAjaxCart(await ajaxCart('cart/change.js', { id: lineId, quantity }));
        else this.apply((await gql(M_UPDATE, { id: this.data.id, lines: [{ id: lineId, quantity }] })).cartLinesUpdate);
        this.emit({ type: 'update', lineId, quantity });
        return this.data;
      });
    },

    remove(lineId) {
      return this.run(async () => {
        if (AJAX_CART) this.data = fromAjaxCart(await ajaxCart('cart/change.js', { id: lineId, quantity: 0 }));
        else this.apply((await gql(M_REMOVE, { id: this.data.id, lineIds: [lineId] })).cartLinesRemove);
        this.emit({ type: 'remove', lineId });
        return this.data;
      });
    },

    /** "Comprar ahora": checkout solo con este producto, sin tocar el carrito actual. */
    async buyNow(merchandiseId, quantity = 1) {
      // En el tema, un enlace permanente de carrito lleva directo al pago con este producto.
      if (AJAX_CART) return `${shopRoot()}cart/${variantNumber(merchandiseId)}:${quantity}`;
      const input = { lines: [{ merchandiseId, quantity }], buyerIdentity: { countryCode: C.country } };
      const payload = (await gql(M_CREATE, { input })).cartCreate;
      if (payload.userErrors?.length || !payload.cart) {
        throw new Error(payload.userErrors?.map((e) => e.message).join(' · ') || 'No se pudo iniciar el pago');
      }
      return payload.cart.checkoutUrl;
    },
  });

  // Al volver desde el checkout con el botón "atrás", el carrito puede haber cambiado.
  window.addEventListener('pageshow', (e) => { if (e.persisted) Cart.refresh(); });

  /* ───────────── Favoritos y vistos recientemente ───────────── */
  const WISH_KEY = 'bw:wish';
  const Wish = createEmitter({
    items: local.get(WISH_KEY, []),
    get count() { return this.items.length; },
    has(handle) { return this.items.some((i) => i.handle === handle); },
    toggle(p) {
      const on = !this.has(p.handle);
      this.items = on
        ? [{
          handle: p.handle, title: p.title, image: p.image, price: p.price, compare: p.compare,
          variantId: p.variantId, type: p.type, typeSlug: p.typeSlug, available: p.available,
        }, ...this.items].slice(0, 60)
        : this.items.filter((i) => i.handle !== p.handle);
      local.set(WISH_KEY, this.items);
      this.emit({ type: on ? 'add' : 'remove', handle: p.handle });
      return on;
    },
  });
  window.addEventListener('storage', (e) => {
    if (e.key === WISH_KEY) { Wish.items = local.get(WISH_KEY, []); Wish.emit({ type: 'sync' }); }
    if (e.key === CART_KEY && !AJAX_CART) Cart.refresh();
  });

  const Recent = {
    KEY: 'bw:recent',
    list() { return local.get(this.KEY, []); },
    push(handle) {
      const next = [handle, ...this.list().filter((h) => h !== handle)].slice(0, 12);
      local.set(this.KEY, next);
    },
  };

  Object.assign(BW, {
    C, R, page, img, link, IN_SHOPIFY, $, $$, esc, norm, slug, clamp, debounce, motionOK, params, local, session,
    money, imgUrl, srcset, gql, mapProduct, register, index, loadCatalog,
    TONES, catMeta, categoriesOf, brandsOf,
    tokenize, stem, searchScore, suggest, highlight,
    getProduct, getRecommendations, getCollection, sanitizeHtml,
    Cart, Wish, Recent,
  });
})();
